/**
 * Feedback inbox — submissions from the "Tell us straight" sheet on the
 * reader sites.
 *
 * Each one is also emailed to service@airage.com when feedback email is
 * switched on, but that email can be off or refused, so this page is the
 * durable copy. Like the Contact inbox it opens on the unread queue.
 */
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { PubBadge } from '../components/PubBadge'
import { ReplyPanel, ReplySettingsPending } from '../components/ReplyPanel'
import { fmtDateTime, fmtRelative } from '../lib/format'
import { InboundBlock } from '../components/InboundBlock'
import { chipFor, legacyTimeline, type InboundMessage, type ReplyState } from '../lib/replies'
import { useRepliesEnabled } from '../lib/useRepliesEnabled'

type FeedbackMessage = {
  id: number
  publication: string
  kind: string
  message: string
  email: string | null
  subscriber_id: number | null
  member_name: string | null
  page_url: string | null
  device: string
  emailed_at: string | null
  status: string
  handled_at: string | null
  handled_by: string | null
  created_at: string | null
  reply_state: ReplyState | null
  inbound: InboundMessage[]
}

type FeedbackList = {
  items: FeedbackMessage[]
  total: number
  unread: number
}

const PUBLICATIONS = [
  { value: '', label: 'All titles' },
  { value: 'flight_journal', label: 'Flight Journal' },
  { value: 'model_airplane_news', label: 'Model Airplane News' },
  { value: 'rc_car_action', label: 'RC Car Action' },
  { value: 'diecast', label: 'Die Cast X' },
]

const STATUSES = [
  { value: 'new', label: 'Unread' },
  { value: 'read', label: 'In progress' },
  { value: 'archived', label: 'Archived' },
  { value: '', label: 'All' },
]

const KINDS = [
  { value: '', label: 'All' },
  { value: 'good', label: 'Good' },
  { value: 'bad', label: 'Bad' },
  { value: 'ugly', label: 'Ugly' },
]

const AUDIENCES = [
  { value: '', label: 'All' },
  { value: 'member', label: 'Members' },
  { value: 'visitor', label: 'Visitors' },
]

// The three chips differ in lightness as well as hue (light green, mid
// amber, deeper red) so they stay distinguishable without colour vision,
// and all read on the dark ground.
const KIND_CHIP: Record<string, { label: string; cls: string }> = {
  good: { label: 'Good', cls: 'text-[#a7f3d0] border-[#a7f3d0]/50 bg-[#a7f3d0]/10' },
  bad: { label: 'Bad', cls: 'text-[#fbbf24] border-[#fbbf24]/50 bg-[#fbbf24]/10' },
  ugly: { label: 'Ugly', cls: 'text-[#f87171] border-[#f87171]/50 bg-[#f87171]/10' },
}

const SELECT_CLS =
  'bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px] min-w-[160px]'

function firstLine(text: string): string {
  const line = text.split('\n').find((l) => l.trim() !== '') ?? ''
  return line.trim()
}

function Filter({
  label, value, onChange, options, minWidth,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  minWidth?: string
}) {
  return (
    <div>
      <div className="label mb-1">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={SELECT_CLS}
        style={minWidth ? { minWidth } : undefined}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

export function FeedbackMessagesPage() {
  const queryClient = useQueryClient()
  // Opens on the queue, not the archive.
  // ?message=<id> (from an email link) opens that message with no status filter.
  const [search] = useSearchParams()
  const linked = Number(search.get('message')) || null
  const [status, setStatus] = useState(linked ? '' : 'new')
  const [publication, setPublication] = useState('')
  const [kind, setKind] = useState('')
  const [audience, setAudience] = useState('')
  const [expanded, setExpanded] = useState<number | null>(linked)

  // The drafted-reply feature 404s when its flag is off; then today's UI stays.
  const replyV2 = useRepliesEnabled()

  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (publication) params.set('publication', publication)
  if (kind) params.set('kind', kind)
  if (audience) params.set('audience', audience)
  const qs = params.toString()

  const list = useQuery({
    queryKey: ['feedback-messages', status, publication, kind, audience],
    queryFn: () =>
      api.get<FeedbackList>(`/api/feedback-messages${qs ? `?${qs}` : ''}`),
  })

  const setStatusFor = useMutation({
    mutationFn: ({ id, next }: { id: number; next: string }) =>
      api.patch<FeedbackMessage>(`/api/feedback-messages/${id}`, { status: next }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-messages'] })
      queryClient.invalidateQueries({ queryKey: ['navrail-feedback-unread'] })
    },
  })

  const items = list.data?.items ?? []

  return (
    <div className="p-8 space-y-6 max-w-[1200px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Readers · inbound</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Feedback
            <span className="text-amber-glow"> ∙ </span>
            <span className="text-paper-trace">{list.data?.unread ?? 0} unread</span>
          </h1>
          <p className="mt-2 text-sm text-paper-dim max-w-[62ch]">
            Feedback from the "Tell us straight" sheet on the reader sites.
            Each is also emailed to service@airage.com when feedback email is
            switched on.
            {replyV2.data === true
              ? ' Each gets a drafted reply. Review it, edit it and send it below; when the reader answers, it shows up here and the message goes back to Unread.'
              : ' Replies, where enabled, come from service@airage.com; answers show up under the message.'}
          </p>
        </div>
        <Link to="/help/reply-desk" className="label hover:underline self-end whitespace-nowrap">Reply desk guide</Link>
      </header>

      <div className="flex flex-wrap gap-4">
        <Filter label="Status" value={status} onChange={setStatus} options={STATUSES} />
        <Filter
          label="Title" value={publication} onChange={setPublication}
          options={PUBLICATIONS} minWidth="220px"
        />
        <Filter label="Kind" value={kind} onChange={setKind} options={KINDS} />
        <Filter label="Who" value={audience} onChange={setAudience} options={AUDIENCES} />
      </div>

      {list.isLoading && <p className="text-paper-trace">Loading…</p>}
      {list.isError && (
        <p className="text-red-400">Couldn't load feedback. Try again.</p>
      )}

      {!list.isLoading && !list.isError && items.length === 0 && (
        <p className="text-paper-trace">
          Nothing here{status === 'new' ? ' — the queue is empty' : ''}.
        </p>
      )}

      <ul className="space-y-3">
        {items.map((m) => {
          const isOpen = expanded === m.id
          const chip = KIND_CHIP[m.kind] ?? { label: m.kind, cls: 'text-paper border-ink-500' }
          return (
            <li key={m.id} className="border border-ink-500 rounded-sm">
              <button
                type="button"
                className="w-full text-left p-4 flex flex-wrap items-baseline gap-x-3 gap-y-1"
                onClick={() => setExpanded(isOpen ? null : m.id)}
                aria-expanded={isOpen}
              >
                {m.status === 'new' && <span className="label-amber">NEW</span>}
                <span
                  className={`font-mono text-[10px] uppercase tracking-label px-1.5 py-0.5 border rounded-sm ${chip.cls}`}
                >
                  {chip.label}
                </span>
                <PubBadge slug={m.publication} compact />
                {(() => { const c = chipFor(m.reply_state); return c && (
                  <span className={`font-mono text-[10px] uppercase tracking-label px-1.5 py-0.5 border rounded-sm ${c.cls}`}>{c.label}</span>
                ) })()}
                <span className="text-paper text-sm">
                  {m.subscriber_id !== null
                    ? `Member · ${m.member_name ?? 'unnamed'}`
                    : 'Visitor'}
                </span>
                <span className="text-paper-dim text-sm truncate max-w-[60ch]">
                  {firstLine(m.message)}
                </span>
                {m.emailed_at === null && (
                  <span className="label text-[10px]">not emailed</span>
                )}
                <span className="ml-auto text-paper-trace text-xs">
                  {m.created_at ? fmtRelative(m.created_at) : ''}
                </span>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 space-y-3 border-t border-ink-500 pt-3">
                  {/* React text node, never HTML; pre-wrap keeps line breaks. */}
                  <p className="text-paper whitespace-pre-wrap break-words">{m.message}</p>
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
                    <dt className="label">Page</dt>
                    <dd className="text-paper-dim break-all">
                      {m.page_url ? (
                        <a
                          className="text-amber-glow hover:underline"
                          href={m.page_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {m.page_url}
                        </a>
                      ) : (
                        '(none)'
                      )}
                    </dd>
                    <dt className="label">Device</dt>
                    <dd className="text-paper-dim">{m.device}</dd>
                    <dt className="label">Reply to</dt>
                    <dd className="text-paper-dim break-all">
                      {m.email ? (
                        <a
                          className="text-amber-glow hover:underline"
                          href={`mailto:${encodeURIComponent(m.email)}`}
                        >
                          {m.email}
                        </a>
                      ) : (
                        '(no email left)'
                      )}
                    </dd>
                    {m.subscriber_id !== null && (
                      <>
                        <dt className="label">Member</dt>
                        <dd className="text-paper-dim">
                          <Link
                            className="text-amber-glow hover:underline"
                            to={`/subscribers/${m.subscriber_id}`}
                          >
                            {m.member_name ?? `Subscriber ${m.subscriber_id}`}
                          </Link>
                        </dd>
                      </>
                    )}
                    <dt className="label">Emailed</dt>
                    <dd className="text-paper-dim">
                      {m.emailed_at ? fmtDateTime(m.emailed_at) : 'not emailed'}
                    </dd>
                    <dt className="label">Received</dt>
                    <dd className="text-paper-dim">
                      {m.created_at ? fmtDateTime(m.created_at) : ''}
                    </dd>
                  </dl>
                  {m.handled_by && (
                    <p className="text-xs text-paper-trace">
                      {m.status} by {m.handled_by}
                      {m.handled_at ? ` · ${fmtRelative(m.handled_at)}` : ''}
                    </p>
                  )}
                  {replyV2.data === false && legacyTimeline([], m.inbound).map((e) => <InboundBlock key={e.key} item={e.inbound!} />)}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {STATUSES.filter((s) => s.value && s.value !== m.status).map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        className="label hover:underline"
                        disabled={setStatusFor.isPending}
                        onClick={() => setStatusFor.mutate({ id: m.id, next: s.value })}
                      >
                        Mark {s.label.toLowerCase()}
                      </button>
                    ))}
                  </div>
                  {replyV2.data === true && <ReplyPanel channel="feedback" messageId={m.id} />}
                  {replyV2.data === undefined && <ReplySettingsPending query={replyV2} />}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
